import { api } from '@/lib/api/client';

// GET /api/visits/:visitId/summary — backend/utils/visitSummary.js.

export type SegmentKey = 'intake' | 'vitals' | 'assessment' | 'wait_doctor' | 'consultation' | 'exit';

export interface VisitSummary {
  visit: {
    visit_code: string;
    visit_type: string;
    current_stage: string;
    started_at: string;
    resolved_at: string | null;
    resolution: string | null;
    resolution_label: string;
    resolution_notes: string | null;
    followup_date: string | null;
    is_new_patient: boolean;
  };
  patient: { name: string; title?: string | null; uhid: string; age: number | null; gender: string | null; mobile: string | null; blood_group: string | null };
  care: { doctor_name: string | null; token: string | number | null; priority: string | null; appointment: boolean; hold_count: number };
  timing: {
    intake_to_doctor_min: number | null;
    wait_doctor_min: number | null;
    consultation_min: number | null;
    total_visit_min: number | null;
    segments: Array<{ key: SegmentKey; label: string; waiting: boolean; minutes: number | null; state: 'done' | 'open' | 'skipped' }>;
    longest_wait: { key: SegmentKey; label: string; minutes: number } | null;
    /** Segment keys open longer than cap_minutes — left open, not real waiting. */
    excluded: string[];
    cap_minutes: number;
  };
  timeline: Array<{ stage: string; label: string; status: string; activated_at: string; completed_at: string | null; completed_by: string | null; minutes: number | null }>;
  presentation: { complaints: string[]; ailment: string | null; pain_scale: string | null; risk_level: string | null; nurse_notes: string | null; current_meds: string | null };
  vitals: Array<{ key: string; label: string; value: string; unit: string; flag: 'high' | 'low' | null }>;
  allergies: Array<{ substance: string; criticality: string | null; reaction: string | null }>;
  diagnoses: Array<{ diagnosis: string; code: string | null; rank: number | null; clinical_status: string | null }>;
  prescription: Array<{ drug: string; strength: string | null; dose: string | null; route: string | null; frequency: string | null; duration: string | null; quantity: number | null; instructions: string | null; status: string }>;
  labs: Array<{ order_number: string; status: string; priority: string | null; ordered_at: string; tests: Array<{ name: string; status: string }> }>;
  documents: Array<{ id: number; doc_type: string; approved_at: string | null; approved_by: string | null; version: number | null }>;
  bills: Array<{ id: number; bill_number: string; bill_type: string; total_amount: number; payment_status: string | null; created_at: string }>;
  billed_total: number;
  admission: { ip_number: string | null; status: string; admitted_at: string; discharged_at: string | null; reason_for_admission: string | null; provisional_diagnosis: string | null; admitting_doctor: string | null } | null;
  rollbacks: Array<{ from_stage: string; to_stage: string; from_label: string; to_label: string; reason: string; done_by: string | null; done_at: string }>;
  highlights: Array<{ tone: 'warn' | 'info' | 'action'; text: string }>;
}

export function fetchVisitSummary(visitCode: string): Promise<VisitSummary> {
  return api.get(`/visits/${encodeURIComponent(visitCode)}/summary`);
}
